import { Fragment } from "react";
import { NavLink } from "react-router";
import { AlertCircle } from "@untitledui/icons";
import { cx } from "@tricura/atlas-ui";
import { GROUP_LABELS, SUBMISSION_SECTIONS, type SectionGroup, type SubmissionSection } from "../pages/submission/sections";

/** Per-group nav layout — controls the divider line between visual
 *  regions. The 4 groups split into 3 regions:
 *    1. Submission       (header)         → divider after
 *    2. Calculations + Policy (2 headers, no internal divider)
 *    3. Files            (no header)
 *  So we draw a divider after `submission` and after `policy`. */
const GROUP_LAYOUT: { group: SectionGroup; dividerAfter: boolean }[] = [
    { group: "submission", dividerAfter: true },
    { group: "calculations", dividerAfter: false },
    { group: "policy", dividerAfter: true },
    { group: "files", dividerAfter: false },
];

/** `scheduleHasWarning` flags the Schedule item with a warning icon
 *  while the Schedule section's duplicate-submission banner is up.
 *
 *  `createMode` is true on `/submissions/new` (before the submission has
 *  a real id). Every section other than UW Information needs a saved
 *  submission id, so in create mode those links are rendered
 *  non-navigable — the user can't leave UW Information until "Save &
 *  Continue" creates the submission. (Otherwise navigating to e.g.
 *  `/submissions/new/schedule` resolves `:id` to the literal "new" and
 *  breaks the section.) Visuals are unchanged; the items just stop
 *  responding to clicks (no `to`, `cursor-not-allowed`). */
const CREATE_MODE_ALLOWED = "uw-information";

export function SubmissionNav({
    scheduleHasWarning = false,
    createMode = false,
    updatedSections,
}: {
    scheduleHasWarning?: boolean;
    createMode?: boolean;
    /** Section ids that just received auto-filled data (OR-1400's
     *  "highlighted sections") — flagged with the same yellow icon as the
     *  Schedule warning, cleared as the user visits each one. */
    updatedSections?: ReadonlySet<string>;
}) {
    const grouped: Record<SectionGroup, SubmissionSection[]> = {
        submission: [],
        calculations: [],
        policy: [],
        files: [],
    };
    for (const section of SUBMISSION_SECTIONS) grouped[section.group].push(section);

    return (
        <nav className="flex w-full flex-col gap-6 lg:w-56 lg:shrink-0" aria-label="Submission sections">
            {GROUP_LAYOUT.map(({ group, dividerAfter }, idx) => {
                const items = grouped[group];
                if (items.length === 0) return null;
                const label = GROUP_LABELS[group];
                return (
                    <Fragment key={group}>
                        {/* `gap-1` is the original link-to-link spacing
                          * (4px). The title gets an explicit
                          * `pb-[14px]` so the title→first-link
                          * boundary totals 18px (14px pb + 4px gap)
                          * without affecting the link-to-link rhythm. */}
                        <div className="flex flex-col gap-1">
                            {label && (
                                <span
                                    className={cx(
                                        "text-section-label px-3 pb-[14px]",
                                        // Only the first group ("Submission")
                                        // gets a touch of breathing room from
                                        // the aside's top padding — designers
                                        // wanted it nudged down by 4px so the
                                        // header sits more comfortably below
                                        // the chrome above.
                                        group === "submission" && "mt-1",
                                    )}
                                >
                                    {label}
                                </span>
                            )}
                            {items.map((section) => {
                                const flagged =
                                    (section.id === "schedule" && scheduleHasWarning) || updatedSections?.has(section.id);
                                const warningIcon = flagged && (
                                    <AlertCircle
                                        role="img"
                                        aria-label={updatedSections?.has(section.id) ? "Has updates" : "Has warnings"}
                                        className="size-4 shrink-0 text-utility-yellow-500"
                                    />
                                );
                                const base =
                                    "flex items-center justify-between gap-2 rounded-md px-3 py-2 text-sm transition duration-100 ease-linear";

                                // Locked in create mode (no submission id yet):
                                // render the same chrome but non-navigable.
                                if (createMode && section.id !== CREATE_MODE_ALLOWED) {
                                    return (
                                        <span
                                            key={section.id}
                                            aria-disabled="true"
                                            className={cx(base, "text-secondary cursor-not-allowed")}
                                        >
                                            <span>{section.label}</span>
                                            {warningIcon}
                                        </span>
                                    );
                                }

                                return (
                                    <NavLink
                                        key={section.id}
                                        to={section.path}
                                        data-autotest={`submission-nav-link-${section.id}`}
                                        className={({ isActive }) =>
                                            cx(
                                                base,
                                                isActive
                                                    ? "bg-brand-primary_alt text-brand-secondary font-semibold"
                                                    : "text-secondary hover:bg-secondary_hover",
                                            )
                                        }
                                    >
                                        <span>{section.label}</span>
                                        {warningIcon}
                                    </NavLink>
                                );
                            })}
                        </div>
                        {dividerAfter && idx < GROUP_LAYOUT.length - 1 && (
                            <hr className="border-tertiary mx-[18px] -my-2" aria-hidden="true" />
                        )}
                    </Fragment>
                );
            })}
        </nav>
    );
}
